// Map data-fetch + derived cluster, extracted verbatim from MapCanvas.tsx
// (step 06 of the MapCanvas decomposition — plan/map-decomp/06-data-and-cleanup.md).
// Pure data-in/data-out: no refs, no PIXI, no effects.
//
// Unlike RoomCanvas's useRoomQueries/useRoomDerivedFurniture split (needed there
// because useRoomHistory had to run between them to preserve hook-call order),
// nothing here interleaves with a hook MapCanvas still calls between these
// queries and their derived memos — so a single useMapQueries hook covers the
// whole cluster; no separate useMapDerived is needed for this step.
import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { getMapObjects, type MapObject } from '../../models/mapObject';
import type { UserData } from '../../models/user';
import type {
  MarketplaceListing,
  ElectionDialogState,
  DailyQuestAssignAndGetResponse,
} from '../../models/apiTypes';
import {
  getListingsMarketplaceListingsGet,
  getUserElectionDialogStateUserElectionDialogGet,
} from '../../client';

/** Те саме правило, що й `mapObjectsSignature` — щоб відрізняти «лише видалення» від змін координат/типу. */
export function getMapObjectRenderSignaturePiece(obj: MapObject): string {
  const mirrorDir = obj.type === 'house' ? (obj.mirror_direction ?? 'left') : '';
  return `${obj.id}:${obj.type}:${obj.x}:${obj.y}:${obj.owner_id ?? 'null'}:${mirrorDir}`;
}

export async function fetchElectionDialogState(): Promise<ElectionDialogState> {
  const { data } = await getUserElectionDialogStateUserElectionDialogGet({ throwOnError: true });
  return { show_election_dialog: data.show_election_dialog ?? false };
}

export function useMapQueries({
  queryClient,
  userData,
  isMayorStoriesBlockedByOnboarding,
}: {
  queryClient: QueryClient;
  userData: UserData | undefined;
  isMayorStoriesBlockedByOnboarding: boolean;
}) {
  const { data: mapObjects = [], isFetching, isPending } = useQuery({
    queryKey: ['mapObjects'],
    queryFn: getMapObjects,
    retry: 3,
  });

  // Marketplace listings — IDs of houses currently for sale
  const { data: marketplaceListings = [] } = useQuery({
    queryKey: ['marketplaceListings'],
    queryFn: async () => {
      const { data } = await getListingsMarketplaceListingsGet({ throwOnError: true });
      return data;
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  const listedHouseIds = useMemo(
    () => new Set(marketplaceListings.filter(l => l.status === 'active').map(l => l.map_object_id)),
    [marketplaceListings]
  );
  const listingsByHouseId = useMemo(
    () => {
      const map = new Map<number, MarketplaceListing>();
      for (const l of marketplaceListings) {
        if (l.status === 'active') map.set(l.map_object_id, l);
      }
      return map;
    },
    [marketplaceListings]
  );

  const plots = useMemo(
    () => {
      return mapObjects.filter(obj => obj.type === 'plot' && obj.owner_id === null);
    },
    [mapObjects]
  );
  const houses = useMemo(() => mapObjects.filter(obj => obj.type === 'house'), [mapObjects]);
  const cityhalls = useMemo(() => mapObjects.filter(obj => obj.type === 'cityhall'), [mapObjects]);
  const shops = useMemo(() => mapObjects.filter(obj => obj.type === 'shop'), [mapObjects]);
  const hellOfFames = useMemo(() => mapObjects.filter(obj => obj.type === 'hell_of_flame'), [mapObjects]);
  const decorObjects = useMemo(() => mapObjects.filter(obj => obj.type.startsWith('decor_')), [mapObjects]);
  const islands = useMemo(() => mapObjects.filter(obj => obj.type.startsWith('island_')), [mapObjects]);

  const mapObjectsSignature = useMemo(
    () => mapObjects.map(obj => getMapObjectRenderSignaturePiece(obj)).join('|'),
    [mapObjects]
  );

  const getLatestMapObject = (mapObject: MapObject | null | undefined): MapObject | null => {
    if (!mapObject) return null;
    const latestMapObjects =
      queryClient.getQueryData<MapObject[]>(['mapObjects']) ?? mapObjects;
    return latestMapObjects.find(obj => obj.id === mapObject.id) ?? mapObject;
  };

  const { data: dailyQuestsData, dataUpdatedAt: dailyQuestsUpdatedAt } = useQuery<DailyQuestAssignAndGetResponse>({
    queryKey: ["dailyQuests"],
    queryFn: async () =>
      queryClient.getQueryData<DailyQuestAssignAndGetResponse>(["dailyQuests"]) ?? { message: "", quests: [] },
    enabled: false,
    staleTime: Infinity,
  });
  const { data: hourlyQuestsData } = useQuery<DailyQuestAssignAndGetResponse>({
    queryKey: ["hourlyQuests"],
    queryFn: async () =>
      queryClient.getQueryData<DailyQuestAssignAndGetResponse>(["hourlyQuests"]) ?? { message: "", quests: [] },
    enabled: false,
    staleTime: Infinity,
  });
  const { data: marketingQuestsData } = useQuery<DailyQuestAssignAndGetResponse>({
    queryKey: ["marketingQuests"],
    queryFn: async () =>
      queryClient.getQueryData<DailyQuestAssignAndGetResponse>(["marketingQuests"]) ?? { message: "", quests: [] },
    enabled: false,
    staleTime: Infinity,
  });
  const listedHouseIdsKey = useMemo(() => [...listedHouseIds].sort().join(','), [listedHouseIds]);
  // Тільки дані мапи — не включаємо режим бога/адміна, щоб перемикання God mode не робило FULL RE-RENDER PIXI.
  const mapObjectsRenderKey = useMemo(() => mapObjectsSignature, [mapObjectsSignature]);

  const { data: electionDialogStateData } = useQuery<ElectionDialogState>({
    queryKey: ['election-dialog-state'],
    queryFn: fetchElectionDialogState,
    enabled: !!userData && !isMayorStoriesBlockedByOnboarding,
    staleTime: 60 * 1000,
  });

  return {
    mapObjects,
    isFetching,
    isPending,
    marketplaceListings,
    listedHouseIds,
    listingsByHouseId,
    plots,
    houses,
    cityhalls,
    shops,
    hellOfFames,
    decorObjects,
    islands,
    mapObjectsSignature,
    getLatestMapObject,
    dailyQuestsData,
    dailyQuestsUpdatedAt,
    hourlyQuestsData,
    marketingQuestsData,
    listedHouseIdsKey,
    mapObjectsRenderKey,
    electionDialogStateData,
  };
}
